import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import { useMode } from '../contexts/ModeContext';
import { useLang } from '../contexts/LangContext';
import { authAPI, quranAPI, hatimAPI } from '../utils/api';
import { getAuthToken } from '../utils/api';
import { ChevronLeft, ChevronRight, Search, BookOpen as BookIcon, Layers, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { speakArabic } from '../utils/tts';

const ReadQuran = () => {
  const { surahNumber, pageNumber } = useParams();
  if (pageNumber) return <SurahReader initialPage={parseInt(pageNumber, 10)} initialMode="page" />;
  if (surahNumber) return <SurahReader surahNumber={parseInt(surahNumber, 10)} />;
  return <SurahList />;
};

const SurahList = () => {
  const { isKidsMode } = useMode();
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [surahs, setSurahs] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        // authAPI.getMe() may fail for guests — that's okay, page is public.
        const meReq = getAuthToken() ? authAPI.getMe().catch(() => ({ data: null })) : Promise.resolve({ data: null });
        const [u, s] = await Promise.all([meReq, quranAPI.getSurahList()]);
        setUser(u.data);
        setSurahs(s.data?.data || []);
      } catch (e) {
        toast.error(lang === 'tr' ? 'Sure listesi yüklenemedi' : 'Failed to load surah list');
      } finally {
        setLoading(false);
      }
    })();
  }, [lang]);

  const filtered = surahs.filter((s) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      s.name_en.toLowerCase().includes(q) ||
      String(s.number).includes(q) ||
      (s.translation_en || '').toLowerCase().includes(q)
    );
  });

  return (
    <AppShell user={user} title={t('quran.surah_list')}>
      <div className="max-w-6xl mx-auto p-4 sm:p-8 md:p-12 space-y-6">
        <header className="mb-6">
          <p className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-2">{lang === 'tr' ? 'Kuran' : 'Quran'}</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {lang === 'tr' ? 'Kuran-ı Kerim Oku' : 'Read the Holy Quran'}
          </h1>
          <p className="text-stone-600 mt-2">
            {lang === 'tr'
              ? '604 sayfa · 114 sure — Mushaf sayfası veya ayet bazlı okuma. Türkçe Diyanet meali eşliğinde.'
              : '604 pages · 114 surahs — Mushaf page or ayah view. With Turkish translation.'}
          </p>
        </header>

        {/* Quick page jump card */}
        <PageJumpCard isKidsMode={isKidsMode} lang={lang} navigate={navigate} />

        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={lang === 'tr' ? 'Sure adı veya numarası ara...' : 'Search surah name or number...'}
            data-testid="surah-search"
            className="w-full pl-11 pr-4 py-3 rounded-xl border border-stone-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40"
          />
        </div>

        {loading ? (
          <div className="text-center py-12 text-stone-500">{t('common.loading')}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="surah-grid">
            {filtered.map((s) => (
              <button
                key={s.number}
                type="button"
                onClick={() => navigate(`/oku/${s.number}`)}
                data-testid={`surah-card-${s.number}`}
                className="text-left p-4 bg-white rounded-2xl border border-stone-200 hover:border-stone-300 hover:shadow-md transition-all flex items-center gap-3"
              >
                <div className={`w-12 h-12 rounded-xl ${isKidsMode ? 'bg-amber-100 text-amber-700' : 'bg-teal-50 text-teal-700'} flex items-center justify-center font-bold text-sm flex-shrink-0`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {s.number}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-stone-900 truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {s.name_en}
                  </div>
                  <div className="text-xs text-stone-500">
                    {s.translation_en} · {s.ayah_count} {lang === 'tr' ? 'ayet' : 'ayahs'} · {s.revelation === 'Meccan' ? t('quran.makki') : t('quran.madani')}
                  </div>
                </div>
                <div className="text-2xl text-stone-700" style={{ fontFamily: 'Amiri, "Noto Naskh Arabic", serif' }}>
                  {s.name_ar.replace('سُورَةُ ', '').replace('ٱ', 'ا').slice(0, 12)}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
};

const PageJumpCard = ({ isKidsMode, lang, navigate }) => {
  const [pageInput, setPageInput] = useState('');
  const accent = isKidsMode ? 'amber' : 'teal';
  const go = () => {
    const n = parseInt(pageInput, 10);
    if (!isNaN(n) && n >= 1 && n <= 604) {
      navigate(`/oku/page/${n}`);
    } else {
      toast.error(lang === 'tr' ? '1-604 arası bir sayfa numarası girin' : 'Enter a page between 1 and 604');
    }
  };

  return (
    <div className={`rounded-2xl border p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 ${isKidsMode ? 'bg-amber-50/60 border-amber-200' : 'bg-teal-50/60 border-teal-200'}`} data-testid="page-jump-card">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${isKidsMode ? 'bg-amber-100 text-amber-700' : 'bg-teal-100 text-teal-800'}`}>
        <FileText className="w-6 h-6" />
      </div>
      <div className="flex-1">
        <h3 className="font-semibold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {lang === 'tr' ? 'Sayfa Numarasıyla Aç' : 'Open by Page Number'}
        </h3>
        <p className="text-sm text-stone-500">
          {lang === 'tr' ? 'Mushaf sayfasını gerçekçi düzende oku (1-604).' : 'Read in realistic Mushaf layout (1-604).'}
        </p>
      </div>
      <div className="flex items-center gap-2 w-full sm:w-auto">
        <input
          type="number"
          min={1}
          max={604}
          value={pageInput}
          onChange={(e) => setPageInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && go()}
          placeholder="1-604"
          data-testid="page-jump-input"
          className="w-full sm:w-28 px-3 py-2 rounded-lg border border-stone-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40"
        />
        <button
          type="button"
          onClick={go}
          data-testid="page-jump-go"
          className={`px-4 py-2 rounded-lg font-semibold text-white text-sm ${isKidsMode ? 'bg-amber-500 hover:bg-amber-600' : 'bg-teal-700 hover:bg-teal-800'}`}
        >
          {lang === 'tr' ? 'Aç' : 'Open'}
        </button>
      </div>
    </div>
  );
};

const SurahReader = ({ surahNumber, initialPage, initialMode }) => {
  const { isKidsMode } = useMode();
  const { lang, mealEdition, t } = useLang();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    if (initialMode === 'page') return 'page';
    try { return localStorage.getItem('quran_view_mode') || 'page'; } catch { return 'page'; }
  });
  const [surahPages, setSurahPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(initialPage || null);
  const isPageOnlyMode = !surahNumber;

  // Inline reciter player state
  const RECITERS = [
    { id: 'ar.alafasy', label: 'Alafasy' },
    { id: 'ar.abdulsamad', label: 'Abdul Basit' },
    { id: 'ar.husarymujawwad', label: 'Husary' },
    { id: 'ar.abdurrahmaansudais', label: 'Sudais' },
  ];
  const [reciter, setReciter] = useState(() => {
    try { return localStorage.getItem('quran_reciter') || 'ar.alafasy'; } catch { return 'ar.alafasy'; }
  });
  const [playingAyah, setPlayingAyah] = useState(null); // {surah, ayah}
  const audioElRef = useRef(null);
  useEffect(() => {
    try { localStorage.setItem('quran_reciter', reciter); } catch (_) { /* ignore */ }
  }, [reciter]);
  const [pageData, setPageData] = useState(null);
  const [arabicData, setArabicData] = useState(null);
  const [mealData, setMealData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMeal, setShowMeal] = useState(() => {
    try { return localStorage.getItem('quran_show_meal') === 'true'; } catch { return false; }
  });
  const [fontSize, setFontSize] = useState(() => {
    try { return parseInt(localStorage.getItem('quran_font_size') || '32', 10); } catch { return 32; }
  });
  const [mealEditionId, setMealEditionId] = useState(() => {
    try { return localStorage.getItem('quran_meal_edition') || 'tr.diyanet'; } catch { return 'tr.diyanet'; }
  });
  const [mealEditions, setMealEditions] = useState([]);

  useEffect(() => {
    try { localStorage.setItem('quran_meal_edition', mealEditionId); } catch (_) { /* ignore */ }
  }, [mealEditionId]);
  useEffect(() => {
    quranAPI.mealEditions?.()
      .then((r) => setMealEditions(r.data?.editions || []))
      .catch(() => {});
  }, []);


  useEffect(() => {
    try { localStorage.setItem('quran_font_size', String(fontSize)); } catch (_) { /* ignore */ }
  }, [fontSize]);
  useEffect(() => {
    try { localStorage.setItem('quran_view_mode', viewMode); } catch (_) { /* ignore */ }
  }, [viewMode]);
  useEffect(() => {
    try { localStorage.setItem('quran_show_meal', String(showMeal)); } catch (_) { /* ignore */ }
  }, [showMeal]);

  // Save reading position to backend (last position for hatim)
  const lastViewedAyahRef = useRef(1);
  const lastSurahRef = useRef(surahNumber);
  useEffect(() => {
    const onUnload = () => {
      try {
        hatimAPI.savePosition(lastSurahRef.current || surahNumber, lastViewedAyahRef.current || 1).catch(() => {});
      } catch (_) { /* ignore */ }
    };
    window.addEventListener('beforeunload', onUnload);
    return () => {
      onUnload();
      window.removeEventListener('beforeunload', onUnload);
    };
  }, [surahNumber]);

  // Load surah's page list once (used by Page Mode) — only when we have a surah
  useEffect(() => {
    if (!surahNumber) return;
    (async () => {
      try {
        const r = await quranAPI.getSurahPages(surahNumber);
        const pages = r.data?.pages || [];
        setSurahPages(pages);
        setCurrentPage((prev) => prev || pages[0] || 1);
      } catch { /* ignore */ }
    })();
  }, [surahNumber]);

  // Load Ayah-mode data (Arabic + meal)
  useEffect(() => {
    if (viewMode !== 'ayah' || !surahNumber) return;
    (async () => {
      try {
        setLoading(true);
        const meReq = getAuthToken() ? authAPI.getMe().catch(() => ({ data: null })) : Promise.resolve({ data: null });
        const [u, ar, ml] = await Promise.all([
          meReq,
          quranAPI.getSurah(surahNumber, 'quran-uthmani'),
          quranAPI.getSurah(surahNumber, mealEdition),
        ]);
        setUser(u.data);
        setArabicData(ar.data);
        setMealData(ml.data);
      } catch (e) {
        toast.error(lang === 'tr' ? 'Sure yüklenemedi' : 'Failed to load surah');
      } finally {
        setLoading(false);
      }
    })();
  }, [surahNumber, mealEdition, lang, viewMode]);

  // Load Page-mode data
  useEffect(() => {
    if (viewMode !== 'page' || !currentPage) return;
    (async () => {
      try {
        setLoading(true);
        const meReq = user ? Promise.resolve({ data: user }) : (getAuthToken() ? authAPI.getMe().catch(() => ({ data: null })) : Promise.resolve({ data: null }));
        const [u, pg] = await Promise.all([
          meReq,
          quranAPI.getPage(currentPage, 'quran-uthmani', true, mealEditionId),
        ]);
        setUser(u.data);
        setPageData(pg.data);
        // Track position from first surah/ayah on page
        const firstGroup = pg.data?.groups?.[0];
        if (firstGroup) {
          lastSurahRef.current = firstGroup.surah_number;
          lastViewedAyahRef.current = firstGroup.ayahs?.[0]?.number_in_surah || 1;
        }
      } catch (e) {
        toast.error(lang === 'tr' ? 'Sayfa yüklenemedi' : 'Failed to load page');
      } finally {
        setLoading(false);
      }
    })();
  }, [currentPage, viewMode, lang, mealEditionId]);

  // Track ayah-in-view (Ayah mode) using IntersectionObserver
  useEffect(() => {
    if (viewMode !== 'ayah' || !arabicData) return;
    const els = document.querySelectorAll('[data-ayah-num]');
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length) {
          const lastVisible = visible[visible.length - 1];
          const n = parseInt(lastVisible.target.getAttribute('data-ayah-num'), 10);
          if (!isNaN(n)) {
            lastViewedAyahRef.current = n;
            lastSurahRef.current = surahNumber;
          }
        }
      },
      { threshold: 0.5 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [arabicData, viewMode, surahNumber]);

  const goToPage = useCallback((n) => {
    if (n >= 1 && n <= 604) setCurrentPage(n);
  }, []);

  const saveCurrentPosition = useCallback(async () => {
    try {
      await hatimAPI.savePosition(lastSurahRef.current || surahNumber || 1, lastViewedAyahRef.current || 1);
      toast.success(lang === 'tr' ? 'Kaldığın yer kaydedildi ✓' : 'Position saved ✓');
    } catch {
      toast.error(lang === 'tr' ? 'Kaydedilemedi' : 'Save failed');
    }
  }, [lang, surahNumber]);

  // Build playlist (surah, ayah pairs) for current view
  const buildPlaylist = useCallback(() => {
    if (viewMode === 'page' && pageData?.groups) {
      const list = [];
      pageData.groups.forEach((g) => {
        (g.ayahs || []).forEach((a) => list.push({ surah: g.surah_number, ayah: a.number_in_surah }));
      });
      return list;
    }
    if (viewMode === 'ayah' && arabicData?.ayahs) {
      return arabicData.ayahs.map((a) => ({ surah: surahNumber, ayah: a.numberInSurah || a.number }));
    }
    return [];
  }, [viewMode, pageData, arabicData, surahNumber]);

  const playFrom = useCallback((idx) => {
    const list = buildPlaylist();
    if (!list.length || idx >= list.length) {
      setPlayingAyah(null);
      return;
    }
    const { surah, ayah } = list[idx];
    const base = ((typeof window !== 'undefined' && window.__hafizim_backend_url) || process.env.REACT_APP_BACKEND_URL || '');
    const url = `${base}/api/quran/v2/audio/${reciter}/${surah}/${ayah}`;
    if (audioElRef.current) {
      try { audioElRef.current.pause(); } catch (_) { /* noop */ }
    }
    const audio = new Audio(url);
    audioElRef.current = audio;
    setPlayingAyah({ surah, ayah });
    audio.onended = () => playFrom(idx + 1);
    audio.onerror = () => {
      toast.error(lang === 'tr' ? 'Ses oynatılamadı' : 'Audio error');
      setPlayingAyah(null);
    };
    audio.play().catch(() => {
      toast.error(lang === 'tr' ? 'Tarayıcı oynatamadı' : 'Browser playback failed');
      setPlayingAyah(null);
    });
  }, [buildPlaylist, reciter, lang]);

  const togglePlayAll = useCallback(() => {
    if (playingAyah) {
      try { audioElRef.current?.pause(); } catch (_) { /* noop */ }
      setPlayingAyah(null);
    } else {
      playFrom(0);
    }
  }, [playingAyah, playFrom]);

  // Cleanup audio on unmount
  useEffect(() => () => {
    try { audioElRef.current?.pause(); } catch (_) { /* noop */ }
  }, []);

  if (loading && !pageData && !arabicData) {
    return (
      <AppShell user={user}>
        <div className="max-w-4xl mx-auto p-8 text-center text-stone-500">{t('common.loading')}</div>
      </AppShell>
    );
  }

  return (
    <AppShell user={user} title={lang === 'tr' ? 'Kuran Oku' : 'Read Quran'}>
      <div className="max-w-3xl mx-auto p-4 sm:p-8 md:p-12 space-y-5">
        <button
          type="button"
          onClick={() => navigate('/oku')}
          data-testid="surah-back"
          className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-stone-900"
        >
          <ChevronLeft className="w-4 h-4" /> {t('quran.surah_list')}
        </button>

        {/* Reader Controls */}
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur rounded-2xl border border-stone-200 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="reader-controls">
          {/* View mode tabs */}
          <div className="flex items-center gap-1 bg-stone-100 rounded-xl p-1" data-testid="view-mode-tabs">
            <button
              type="button"
              onClick={() => setViewMode('page')}
              data-testid="mode-page"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                viewMode === 'page'
                  ? (isKidsMode ? 'bg-amber-500 text-white' : 'bg-teal-700 text-white')
                  : 'text-stone-600 hover:bg-white'
              }`}
            >
              <FileText className="w-3.5 h-3.5" /> {lang === 'tr' ? 'Sayfa Modu' : 'Page Mode'}
            </button>
            <button
              type="button"
              onClick={() => setViewMode('ayah')}
              data-testid="mode-ayah"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                viewMode === 'ayah'
                  ? (isKidsMode ? 'bg-amber-500 text-white' : 'bg-teal-700 text-white')
                  : 'text-stone-600 hover:bg-white'
              }`}
            >
              <Layers className="w-3.5 h-3.5" /> {lang === 'tr' ? 'Ayet Modu' : 'Ayah Mode'}
            </button>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wider text-stone-500">{lang === 'en' ? 'Size' : 'Boyut'}</span>
            <button type="button" onClick={() => setFontSize((s) => Math.max(18, s - 4))} className="w-8 h-8 rounded-lg border border-stone-200 hover:bg-stone-100 font-bold text-stone-700" data-testid="font-decrease">−</button>
            <span className="text-sm font-semibold w-10 text-center text-stone-700">{fontSize}</span>
            <button type="button" onClick={() => setFontSize((s) => Math.min(72, s + 4))} className="w-8 h-8 rounded-lg border border-stone-200 hover:bg-stone-100 font-bold text-stone-700" data-testid="font-increase">+</button>
          </div>

          <button
            type="button"
            onClick={togglePlayAll}
            data-testid="play-all-btn"
            className={`px-3 py-2 rounded-xl text-sm font-semibold border transition-colors inline-flex items-center gap-1.5 ${playingAyah ? (isKidsMode ? 'bg-amber-500 text-white border-amber-600' : 'bg-teal-700 text-white border-teal-800') : 'bg-white text-stone-700 border-stone-200 hover:border-stone-300'}`}
            title={lang === 'tr' ? 'Sırayla çal/durdur' : 'Play/Stop sequential'}
          >
            {playingAyah ? '⏸' : '▶'} {playingAyah ? `${playingAyah.surah}:${playingAyah.ayah}` : (lang === 'tr' ? 'Çal' : 'Play')}
          </button>

          <select
            value={reciter}
            onChange={(e) => setReciter(e.target.value)}
            data-testid="reciter-select"
            className="text-xs px-2 py-2 rounded-xl border border-stone-200 bg-white text-stone-700"
          >
            {RECITERS.map((r) => (
              <option key={r.id} value={r.id}>{r.label}</option>
            ))}
          </select>

          <button
            type="button"
            onClick={() => {
              const surahForListen = viewMode === 'page'
                ? (pageData?.groups?.[0]?.surah_number || lastSurahRef.current)
                : surahNumber;
              if (surahForListen) navigate(`/dinle/${surahForListen}`);
            }}
            data-testid="listen-btn"
            title={lang === 'tr' ? 'Bu sureyi dinle' : 'Listen to this surah'}
            className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-colors inline-flex items-center gap-1.5 ${isKidsMode ? 'bg-amber-50 text-amber-800 border-amber-200 hover:border-amber-400' : 'bg-teal-50 text-teal-800 border-teal-200 hover:border-teal-400'}`}
          >
            🎧 {lang === 'tr' ? 'Dinle' : 'Listen'}
          </button>

          <button
            type="button"
            onClick={saveCurrentPosition}
            data-testid="save-position-btn"
            className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-colors ${isKidsMode ? 'bg-amber-50 text-amber-800 border-amber-200 hover:border-amber-400' : 'bg-teal-50 text-teal-800 border-teal-200 hover:border-teal-400'}`}
            title={lang === 'tr' ? 'Şu anki konumu hatim ilerlemesine kaydet' : 'Save current position to hatim progress'}
          >
            💾 {lang === 'tr' ? 'Kaldığım Yer' : 'Save Position'}
          </button>

          <button
            type="button"
            onClick={() => setShowMeal((v) => !v)}
            data-testid="meal-toggle"
            className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-colors ${showMeal ? (isKidsMode ? 'bg-amber-500 text-white border-amber-600' : 'bg-teal-700 text-white border-teal-800') : 'bg-white text-stone-700 border-stone-200 hover:border-stone-300'}`}
          >
            {showMeal ? (lang === 'en' ? '✓ Translation On' : '✓ Meal Açık') : (lang === 'en' ? 'Show Translation' : 'Meali Göster')}
          </button>

          {showMeal && mealEditions.length > 0 && (
            <select
              value={mealEditionId}
              onChange={(e) => setMealEditionId(e.target.value)}
              data-testid="meal-edition-select"
              className="text-xs px-2 py-2 rounded-xl border border-stone-200 bg-white text-stone-700 max-w-[180px]"
              title={lang === 'tr' ? 'Çeviri / Meal' : 'Translation edition'}
            >
              {mealEditions.map((ed) => (
                <option key={ed.id} value={ed.id}>
                  {lang === 'en' ? ed.label_en : ed.label_tr}
                </option>
              ))}
            </select>
          )}
        </div>

        {viewMode === 'page' ? (
          <PageView
            pageData={pageData}
            currentPage={currentPage}
            surahPages={surahPages}
            loading={loading}
            showMeal={showMeal}
            fontSize={fontSize}
            isKidsMode={isKidsMode}
            lang={lang}
            goToPage={goToPage}
          />
        ) : (
          <AyahView
            arabicData={arabicData}
            mealData={mealData}
            showMeal={showMeal}
            fontSize={fontSize}
            isKidsMode={isKidsMode}
            lang={lang}
            t={t}
          />
        )}

        <p className="text-center text-xs text-stone-400 py-6">
          {lang === 'tr'
            ? 'Kaynak: alquran.cloud — Türkçe meal: Diyanet İşleri Başkanlığı'
            : 'Source: alquran.cloud — English: Sahih International'}
        </p>
      </div>
    </AppShell>
  );
};

// ============= PAGE VIEW (Mushaf) =============
const PageView = ({ pageData, currentPage, surahPages, loading, showMeal, fontSize, isKidsMode, lang, goToPage }) => {
  if (loading || !pageData) {
    return <div className="text-center py-16 text-stone-500">{lang === 'tr' ? 'Yükleniyor...' : 'Loading...'}</div>;
  }

  return (
    <>
      {/* Page navigation */}
      <div className="flex items-center justify-between gap-3 bg-white rounded-2xl border border-stone-200 p-3" data-testid="page-nav">
        <button
          type="button"
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage <= 1}
          data-testid="page-prev"
          className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold text-stone-700 hover:bg-stone-100 disabled:opacity-40"
        >
          <ChevronLeft className="w-4 h-4" /> {lang === 'tr' ? 'Önceki' : 'Prev'}
        </button>
        <div className="text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-stone-500">{lang === 'tr' ? 'Sayfa' : 'Page'}</div>
          <div className="text-lg font-bold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{currentPage} / 604</div>
        </div>
        <button
          type="button"
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage >= 604}
          data-testid="page-next"
          className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold text-stone-700 hover:bg-stone-100 disabled:opacity-40"
        >
          {lang === 'tr' ? 'Sonraki' : 'Next'} <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Mushaf-style page */}
      <article
        className="bg-gradient-to-b from-amber-50/40 via-white to-amber-50/40 rounded-3xl border-2 border-amber-200/80 shadow-inner p-6 sm:p-10"
        style={{ boxShadow: 'inset 0 0 0 1px rgba(217,170,80,0.15), 0 4px 12px rgba(120,80,20,0.06)' }}
        data-testid={`page-${currentPage}`}
      >
        {pageData.groups?.map((group) => (
          <section key={group.surah_number} className="mb-6 last:mb-0">
            {/* Surah header */}
            <div className="text-center mb-5 pb-3 border-b-2 border-dashed border-amber-300/60">
              <div className="text-xs uppercase tracking-[0.25em] text-stone-500">{lang === 'tr' ? 'Sure' : 'Surah'} {group.surah_number}</div>
              <h2 className={`text-3xl mt-1 ${isKidsMode ? 'text-amber-700' : 'text-teal-800'}`} style={{ fontFamily: 'Amiri, serif', direction: 'rtl' }}>
                {group.surah_name_ar}
              </h2>
              <p className="text-sm text-stone-600 mt-0.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{group.surah_name_en}</p>
              {/* Bismillah for new surahs except 1, 9 */}
              {group.surah_number !== 1 && group.surah_number !== 9 && group.ayahs?.[0]?.number_in_surah === 1 && (
                <p className="text-xl mt-3 text-stone-700" style={{ fontFamily: 'Amiri, serif', direction: 'rtl' }}>
                  بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
                </p>
              )}
            </div>

            {/* Continuous Arabic body — true Mushaf flow */}
            <p
              className="text-stone-900 selection:bg-amber-200/50"
              style={{
                fontFamily: '"Amiri Quran", Amiri, "Noto Naskh Arabic", serif',
                direction: 'rtl',
                fontSize: `${fontSize}px`,
                lineHeight: 2.2,
                textAlign: 'justify',
                textAlignLast: 'right',
                wordSpacing: '0.05em',
              }}
            >
              {group.ayahs?.map((a, idx) => (
                <span key={`${group.surah_number}-${a.number_in_surah}`}>
                  <span className={isKidsMode ? 'text-amber-900' : 'text-stone-900'}>{a.text}</span>
                  <span
                    className={`inline-flex items-center justify-center mx-1 text-[0.5em] font-bold rounded-full w-7 h-7 align-middle ${isKidsMode ? 'bg-amber-100 text-amber-800 border border-amber-300' : 'bg-teal-50 text-teal-800 border border-teal-200'}`}
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    ۝{a.number_in_surah}
                  </span>
                  {idx < group.ayahs.length - 1 ? ' ' : ''}
                </span>
              ))}
            </p>

            {/* Translation block (optional) */}
            {showMeal && (
              <div className="mt-5 pt-4 border-t border-stone-200 space-y-2">
                {group.ayahs?.map((a) => a.meal && (
                  <div key={`meal-${group.surah_number}-${a.number_in_surah}`} className="flex gap-3 text-sm text-stone-700 leading-relaxed group">
                    <span className={`inline-flex items-center justify-center w-7 h-7 flex-shrink-0 rounded-full text-xs font-bold ${isKidsMode ? 'bg-amber-100 text-amber-800' : 'bg-teal-50 text-teal-800'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {a.number_in_surah}
                    </span>
                    <p className="flex-1">{a.meal}</p>
                    <button
                      type="button"
                      onClick={() => speakArabic(a.text, { onError: () => {} })}
                      data-testid={`ayah-tts-${group.surah_number}-${a.number_in_surah}`}
                      title={lang === 'tr' ? 'Bu ayeti TTS ile dinle (eğitim)' : 'Listen with educational TTS'}
                      className="flex-shrink-0 w-7 h-7 rounded-full bg-stone-100 hover:bg-amber-200 text-stone-500 hover:text-amber-700 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      🔊
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        ))}
      </article>

      {/* Surah's pages quick nav */}
      {surahPages.length > 1 && (
        <div className="bg-white rounded-2xl border border-stone-200 p-4" data-testid="surah-page-list">
          <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2">{lang === 'tr' ? 'Bu surenin sayfaları' : 'Pages of this surah'}</div>
          <div className="flex flex-wrap gap-2">
            {surahPages.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => goToPage(p)}
                data-testid={`surah-page-${p}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                  p === currentPage
                    ? (isKidsMode ? 'bg-amber-500 text-white border-amber-600' : 'bg-teal-700 text-white border-teal-800')
                    : 'bg-white text-stone-700 border-stone-200 hover:border-stone-400'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

// ============= AYAH VIEW (legacy linear list) =============
const AyahView = ({ arabicData, mealData, showMeal, fontSize, isKidsMode, lang, t }) => {
  if (!arabicData) return <div className="text-center py-12 text-stone-500">{t('common.loading')}</div>;
  return (
    <>
      <header className="text-center py-6 border-b border-stone-200">
        <p className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-1">Sure {arabicData.number}</p>
        <h1 className={`text-3xl sm:text-4xl font-bold ${isKidsMode ? 'text-amber-700' : 'text-teal-800'}`} style={{ fontFamily: 'Amiri, serif', direction: 'rtl' }}>
          {arabicData.name_ar}
        </h1>
        <p className="text-stone-600 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {arabicData.name_en}
        </p>
      </header>

      <div className="space-y-6">
        {arabicData.ayahs.map((ayah, idx) => {
          const meal = mealData?.ayahs?.[idx];
          return (
            <div key={ayah.number} data-ayah-num={ayah.number} className="bg-white rounded-2xl border border-stone-200 p-6 sm:p-8" data-testid={`ayah-${ayah.number}`}>
              <div className="flex items-start justify-between mb-4">
                <span className={`inline-flex items-center justify-center w-9 h-9 rounded-full text-sm font-bold ${isKidsMode ? 'bg-amber-100 text-amber-800' : 'bg-teal-50 text-teal-800'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {ayah.number}
                </span>
                <button
                  type="button"
                  onClick={() => speakArabic(ayah.text, { onError: () => {} })}
                  data-testid={`ayah-tts-btn-${ayah.number}`}
                  title="TTS ile dinle"
                  className="w-9 h-9 rounded-full bg-stone-50 hover:bg-amber-100 text-stone-600 hover:text-amber-700 flex items-center justify-center text-base"
                >
                  🔊
                </button>
              </div>
              <p
                className="text-right text-stone-900 mb-4"
                style={{ fontFamily: '"Amiri Quran", Amiri, "Noto Naskh Arabic", serif', direction: 'rtl', fontSize: `${fontSize}px`, lineHeight: 2.0 }}
              >
                {ayah.text}
              </p>
              {showMeal && meal && (
                <p className="text-base text-stone-600 leading-relaxed border-t border-stone-100 pt-4">
                  {meal.text}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default ReadQuran;
